import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  Clock,
  KeyRound,
  Loader2,
  RefreshCw,
  Users,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { apiFetch, ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { formatNumber, formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

// ── DTOs (mirror Javeed.Shared.Organizations.SyncStatusDto) ────────────────

interface SyncStatusDto {
  generatedAt: string;
  companies: CompanySyncStatusDto[];
}

interface CompanySyncStatusDto {
  companyId: string;
  companyName: string;
  ordioId: number;
  isStale: boolean;
  lastSeenAt: string | null;
  lastBaseDataSyncedAt: string | null;
  lastBranchesSyncedAt: string | null;
  lastShiftsFullRefetchedAt: string | null;
  lastShiftsSinceCursor: string | null;
  counts: SyncCountsDto;
  sampleEmployees: SyncSampleEmployeeDto[];
}

interface SyncCountsDto {
  branches: number;
  workingAreas: number;
  identitiesActive: number;
  identitiesInactive: number;
  snapshots: number;
  activeWages: number;
  wageHistoryEntries: number;
  effectiveApiKeySources: number;
}

interface SyncSampleEmployeeDto {
  ordioEmployeeId: string;
  displayName: string;
  lastSyncedAt: string;
  hasSnapshot: boolean;
  hasActiveWage: boolean;
  activeWageType: string | null;
  activeWageAmount: number | null;
  wageHistoryCount: number;
}

export const Route = createFileRoute("/o/$slug/sync-status")({
  component: SyncStatusPage,
});

function SyncStatusPage() {
  const org = useOrg();
  const sync = useQuery({
    queryKey: ["sync-status", org.slug],
    queryFn: () => apiFetch<SyncStatusDto>(`/api/o/${org.slug}/sync-status`),
    refetchOnMount: "always",
  });

  return (
    <PageContainer>
      <PageHeader
        title="Sync-Status"
        description="Hintergrund-Sweeps von Ordio in Javeed. Aktualisiert sich beim Öffnen der Seite."
        actions={
          <Button
            variant="outline"
            onClick={() => void sync.refetch()}
            disabled={sync.isFetching}
          >
            {sync.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Aktualisieren
          </Button>
        }
      />

      {sync.data && (
        <p className="mb-4 text-xs text-muted-foreground">
          Generiert {formatRelative(sync.data.generatedAt) ?? "—"}
        </p>
      )}

      {sync.isLoading && <PageSkeleton />}

      {sync.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <div className="flex items-start gap-2 text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {sync.error instanceof ApiError && sync.error.status === 403
                ? "Keine Berechtigung."
                : `Konnte Sync-Status nicht laden: ${(sync.error as Error).message}`}
            </span>
          </div>
        </div>
      )}

      {sync.data && sync.data.companies.length === 0 && (
        <div className="rounded-md border bg-card p-12 text-center">
          <RefreshCw className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Noch keine Unternehmen synchronisiert. API-Schlüssel in den Org-Einstellungen
            hinterlegen, damit die Sweeps Daten haben, womit sie arbeiten können.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {sync.data?.companies.map((c) => <CompanySyncCard key={c.companyId} company={c} />)}
      </div>
    </PageContainer>
  );
}

// ── One company card ───────────────────────────────────────────────────────

function CompanySyncCard({ company }: { company: CompanySyncStatusDto }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              {company.companyName}
              {company.isStale ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                  <AlertTriangle className="h-3 w-3" />
                  Veraltet
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  Aktiv
                </span>
              )}
            </CardTitle>
            <CardDescription>Ordio-ID {company.ordioId}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <CountsGrid counts={company.counts} />
        <Separator />
        <TierTimestamps company={company} />
        <Separator />
        <SampleEmployees employees={company.sampleEmployees} />
      </CardContent>
    </Card>
  );
}

// ── Counts grid ────────────────────────────────────────────────────────────

function CountsGrid({ counts }: { counts: SyncCountsDto }) {
  const cells: Array<{
    icon?: React.ReactNode;
    label: string;
    value: number;
    accent?: "muted";
  }> = [
    { icon: <Users className="h-3.5 w-3.5" />, label: "Mitarbeitende", value: counts.identitiesActive },
    { icon: <Users className="h-3.5 w-3.5" />, label: "Inaktiv", value: counts.identitiesInactive, accent: "muted" },
    { icon: <Briefcase className="h-3.5 w-3.5" />, label: "Filialen", value: counts.branches },
    { icon: <Briefcase className="h-3.5 w-3.5" />, label: "Arbeitsbereiche", value: counts.workingAreas },
    { label: "Snapshots", value: counts.snapshots },
    { label: "Aktive Lohnregeln", value: counts.activeWages },
    { label: "Lohnhistorie", value: counts.wageHistoryEntries },
    {
      icon: <KeyRound className="h-3.5 w-3.5" />,
      label: "API-Keys",
      value: counts.effectiveApiKeySources,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {cells.map((c) => (
        <div
          key={c.label}
          className={cn(
            "rounded-md border bg-background p-3",
            c.accent === "muted" && "opacity-60",
          )}
        >
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {c.icon}
            {c.label}
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums">{formatNumber(c.value)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Tier timestamps ────────────────────────────────────────────────────────

function TierTimestamps({ company }: { company: CompanySyncStatusDto }) {
  // The tier labels match Program.cs's recurring jobs. Operators don't think
  // in hot/warm/cold — surface the concept ("Stammdaten alle 15 Min", "Hot
  // jede Minute") via the cadence column instead.
  const rows: Array<{ label: string; cadence: string; iso: string | null }> = [
    {
      label: "Stammdaten",
      cadence: "alle 15 Min",
      iso: company.lastBaseDataSyncedAt,
    },
    {
      label: "Filialen & Bereiche",
      cadence: "alle 6 Std",
      iso: company.lastBranchesSyncedAt,
    },
    {
      label: "Schichten (voll)",
      cadence: "jede Minute",
      iso: company.lastShiftsFullRefetchedAt,
    },
    {
      label: "Letzter Kontakt",
      cadence: "",
      iso: company.lastSeenAt,
    },
  ];
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
        Sweep-Zeitstempel
      </h3>
      <dl className="overflow-hidden rounded-md border bg-background text-sm">
        {rows.map((r, i) => (
          <div
            key={r.label}
            className={cn(
              "flex items-baseline justify-between gap-2 px-3 py-2",
              i > 0 && "border-t",
            )}
          >
            <dt>
              {r.label}
              {r.cadence && (
                <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {r.cadence}
                </span>
              )}
            </dt>
            <dd className="text-xs text-muted-foreground">{formatRelative(r.iso) ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── Sample employees ───────────────────────────────────────────────────────

function SampleEmployees({ employees }: { employees: SyncSampleEmployeeDto[] }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Stichprobe
      </h3>
      {employees.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Keine Beispieldaten — der Warm-Sweep hat noch nicht gelaufen.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border bg-background">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-right font-medium">Aktiver Lohn</th>
                <th className="px-3 py-2 text-right font-medium">Lohnhistorie</th>
                <th className="px-3 py-2 text-left font-medium">Letzter Sync</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {employees.map((e) => (
                <tr key={e.ordioEmployeeId}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{e.displayName}</div>
                    <div className="text-[10px] text-muted-foreground">
                      ID {e.ordioEmployeeId}
                      {!e.hasSnapshot && (
                        <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">
                          kein Snapshot
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {e.hasActiveWage && e.activeWageAmount != null
                      ? `${formatNumber(e.activeWageAmount)} ${e.activeWageType ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatNumber(e.wageHistoryCount)}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {formatRelative(e.lastSyncedAt) ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, i) => (
        <Skeleton key={i} className="h-64" />
      ))}
    </div>
  );
}
